import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CIF } from "@/constants/testIds";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowUpRight, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadDialog = ({ open, onOpenChange, intent = "create-workspace" }) => {
  const [form, setForm] = useState({ full_name: "", work_email: "", company: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/workspace/leads`, { ...form, intent });
      toast.success("Workspace request received", {
        description: "We'll send your access link to " + form.work_email,
      });
      onOpenChange(false);
      setForm({ full_name: "", work_email: "", company: "" });
    } catch (err) {
      toast.error("Could not submit", {
        description: err?.response?.data?.detail?.[0]?.msg || "Please check your details and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={CIF.leadDialog} className="sm:max-w-[460px] border-slate-200 rounded-2xl">
        <DialogHeader>
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-blue-700 font-semibold mb-2">
            <ShieldCheck className="h-3.5 w-3.5" /> Workspace Access
          </div>
          <DialogTitle className="font-display text-2xl font-medium text-slate-900 leading-snug">
            Create your free DeMaxtore Workspace
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Get instant access to Estimated CIF™, RFQ™, CommodityBid™ and the full procurement intelligence suite.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="lead-name" className="text-xs font-medium text-slate-700">Full name</Label>
            <Input
              id="lead-name"
              data-testid={CIF.leadInputName}
              required
              minLength={2}
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="Jane Doe"
              className="rounded-lg border-slate-200 focus-visible:ring-blue-500/30"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lead-email" className="text-xs font-medium text-slate-700">Work email</Label>
            <Input
              id="lead-email"
              data-testid={CIF.leadInputEmail}
              required
              type="email"
              value={form.work_email}
              onChange={(e) => setForm({ ...form, work_email: e.target.value })}
              placeholder="jane@company.com"
              className="rounded-lg border-slate-200 focus-visible:ring-blue-500/30"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lead-company" className="text-xs font-medium text-slate-700">Company <span className="text-slate-400 font-normal">· optional</span></Label>
            <Input
              id="lead-company"
              data-testid={CIF.leadInputCompany}
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              placeholder="Acme Trading Co."
              className="rounded-lg border-slate-200 focus-visible:ring-blue-500/30"
            />
          </div>

          <Button
            data-testid={CIF.leadSubmit}
            type="submit"
            disabled={loading}
            className="btn-shine w-full rounded-lg bg-slate-900 text-white hover:bg-slate-800 h-11 font-medium"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Open my Workspace <ArrowUpRight className="ml-1.5 h-4 w-4" />
              </>
            )}
          </Button>

          <p className="text-[11px] text-slate-400 text-center pt-1">
            No credit card. SSO &amp; SOC2 controls available on Business plans.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LeadDialog;
